import { useMutation } from "@apollo/client";
import { compact } from "lodash";
import { useMemo, useState } from "react";
import { useCallback } from "react";
import {
  ComplianceProgramListItemFragment,
  ComplianceProgramState,
  DeleteProgramMutation,
  DeleteProgramMutationVariables,
  GetComplianceProgramQuery,
  SaveComplianceProgramMutation,
  SaveComplianceProgramMutationVariables,
} from "types/graphql-schema";

import { currentOrganizationVar, successNotificationVar } from "@cache/cache";
import Dropdown from "@components/dropdown/dropdown";
import { removeConsecutiveDividers } from "@components/dropdown/dropdown-helpers";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { useLink } from "@components/link/link-helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import createOrUpdateComplianceProgramMutation from "../graphql/create-or-update-compliance-program-mutation";
import deleteComplianceProgramMutation from "../graphql/delete-compliance-program-mutation";
import getComplianceProgramQuery from "../graphql/get-compliance-program-admin-query";
import getComplianceProgramsQuery from "../graphql/get-compliance-programs-query";
import ComplianceProgramDetailsDialog from "./compliance-program-details-dialog";
import ComplianceProgramUserChangesDialog from "./compliance-program-user-changes-dialog";

const ComplianceProgramActionDropdown = ({
  complianceProgram,
}: {
  complianceProgram:
    | NonNullable<GetComplianceProgramQuery["complianceProgram"]>
    | ComplianceProgramListItemFragment;
}) => {
  const link = useLink();
  const organization = currentOrganizationVar();

  const complianceProgramId = complianceProgram.id;
  const complianceProgramState = complianceProgram.state;
  const complianceProgramTitle = complianceProgram.title;
  const canUpdate = complianceProgram.canUpdate;
  const canDelete = complianceProgram.canDelete;
  const availableAppliesToUpdates =
    "availableAppliesToUpdates" in complianceProgram
      ? complianceProgram.availableAppliesToUpdates
      : undefined;

  const [
    isShowingProgramUserChangesDialog,
    setIsShowingProgramUserChangesDialog,
  ] = useState(false);
  const [isShowingProgramDetailsDialog, setIsShowingProgramDetailsDialog] =
    useState(false);

  const { ConfirmationDialog: ConfirmDeleteDialog, confirm: confirmDelete } =
    useConfirm(
      "Are you sure?",
      "Are you sure you want to delete this program? All assessment data will be deleted.",
    );

  const [saveComplianceProgram, { loading: isPublishingProgram }] = useMutation<
    SaveComplianceProgramMutation,
    SaveComplianceProgramMutationVariables
  >(createOrUpdateComplianceProgramMutation);

  const [deleteProgram, { loading: isDeletingProgram }] = useMutation<
    DeleteProgramMutation,
    DeleteProgramMutationVariables
  >(deleteComplianceProgramMutation);

  const errorMatches = useMemo(
    () => [
      {
        match: "no actions to take",
        title: "Add at least one step to the program before publishing",
      },
    ],
    [],
  );

  const publishProgram = useCallback(() => {
    saveComplianceProgram({
      variables: {
        complianceProgramId,
        state: ComplianceProgramState.Published,
        organizationId: organization.id,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramQuery],
      onCompleted: () =>
        successNotificationVar({
          title: "Program published",
        }),
    });
  }, [complianceProgramId, organization, errorMatches, saveComplianceProgram]);

  const handleDeleteProgram = useCallback(async () => {
    const confirm = await confirmDelete();
    if (!confirm) return;
    deleteProgram({
      variables: {
        complianceProgramId,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramsQuery],
      onCompleted: () => {
        successNotificationVar({ title: "Program deleted" });
        link.redirect(`/programs`);
      },
    });
  }, [confirmDelete, deleteProgram, complianceProgramId, errorMatches, link]);

  const handlePauseProgram = useCallback(async () => {
    saveComplianceProgram({
      variables: {
        complianceProgramId,
        state: ComplianceProgramState.Paused,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramQuery],
      onCompleted: () =>
        successNotificationVar({
          title: "Program paused",
        }),
    });
  }, [saveComplianceProgram, complianceProgramId, errorMatches]);

  const dropdownOptions: DropdownOptionType[] = useMemo(() => {
    return removeConsecutiveDividers(
      compact([
        {
          label: "Duplicate program",
          onClick: () =>
            link.redirect(`/programs/${complianceProgramId}/duplicate`),
          disabled: isPublishingProgram || isDeletingProgram,
        },
        canUpdate?.permission && {
          label: "Edit program",
          onClick: () => link.redirect(`/programs/${complianceProgramId}/edit`),
          disabled: isPublishingProgram || isDeletingProgram,
        },
        canUpdate?.permission &&
          complianceProgramState === ComplianceProgramState.Published && {
            label: "Pause program",
            onClick: handlePauseProgram,
            disabled: isPublishingProgram || isDeletingProgram,
          },
        canUpdate?.permission &&
          complianceProgramState !== ComplianceProgramState.Published && {
            label:
              complianceProgramState === ComplianceProgramState.Draft
                ? "Publish"
                : "Resume",
            onClick: publishProgram,
            disabled: isPublishingProgram,
          },
        { type: "divider" },
        availableAppliesToUpdates?.hasUpdates && {
          label: "Update Program Participants",
          onClick: () => setIsShowingProgramUserChangesDialog(true),
        },
        {
          label: "View Program Details",
          onClick: () => setIsShowingProgramDetailsDialog(true),
        },
        { type: "divider" },
        canDelete?.permission && {
          label: "Delete program",
          onClick: handleDeleteProgram,
          textColor: "text-red-700 hover:text-red-900",
          disabled: isPublishingProgram || isDeletingProgram,
        },
      ]),
    );
  }, [
    canDelete,
    canUpdate,
    complianceProgramId,
    complianceProgramState,
    availableAppliesToUpdates,
    handleDeleteProgram,
    handlePauseProgram,
    isDeletingProgram,
    isPublishingProgram,
    link,
    publishProgram,
  ]);

  if (dropdownOptions.length === 0) return null;

  return (
    <>
      <Dropdown
        options={dropdownOptions}
        aria-label="Compliance program actions"
      />
      <ConfirmDeleteDialog />
      {isShowingProgramUserChangesDialog && availableAppliesToUpdates && (
        <ComplianceProgramUserChangesDialog
          onClose={() => setIsShowingProgramUserChangesDialog(false)}
          complianceProgramId={complianceProgramId}
          complianceProgramTitle={complianceProgramTitle}
          availableAppliesToUpdates={availableAppliesToUpdates}
        />
      )}
      {isShowingProgramDetailsDialog && (
        <ComplianceProgramDetailsDialog
          complianceProgram={complianceProgram}
          onClose={() => setIsShowingProgramDetailsDialog(false)}
        />
      )}
    </>
  );
};
export default ComplianceProgramActionDropdown;
